// /app/frontend/src/components/frugal-v2/components/ScheduledChargesModal.jsx
// Scheduled Charges Modal - Refactored (150 lines)

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Calendar, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { translations } from './scheduled/scheduledTranslations';
import ScheduledChargeForm from './scheduled/ScheduledChargeForm';
import ScheduledChargeItem from './scheduled/ScheduledChargeItem';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ScheduledChargesModal({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [charges, setCharges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '', amount: '', category: 'Impuestos',
    month: new Date().getMonth() + 1, day: 1, year: '', notes: ''
  });

  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const t = translations[lang];
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) loadCharges();
  }, [isOpen]);

  const loadCharges = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/v2/frugal/scheduled`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setCharges((await response.json()).charges || []);
    } catch (err) {
      console.error('Error loading charges:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', amount: '', category: 'Impuestos', month: new Date().getMonth() + 1, day: 1, year: '', notes: '' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingId 
        ? `${API}/api/v2/frugal/scheduled/${editingId}`
        : `${API}/api/v2/frugal/scheduled`;
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          year: formData.year ? parseInt(formData.year) : null
        })
      });
      if (response.ok) {
        toast.success(t.save);
        loadCharges();
        resetForm();
      }
    } catch (err) {
      toast.error('Error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (charge) => {
    setFormData({
      name: charge.name, amount: charge.amount.toString(), category: charge.category,
      month: charge.month, day: charge.day, year: charge.year?.toString() || '', notes: charge.notes || ''
    });
    setEditingId(charge.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await fetch(`${API}/api/v2/frugal/scheduled/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      loadCharges();
    } catch (err) {
      toast.error('Error');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] bg-slate-900 rounded-2xl border border-white/10 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" /> {t.title}
            </h2>
            <p className="text-sm text-slate-400">{t.subtitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Add Button */}
          {!showForm && (
            <button onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded-xl border border-emerald-500/30">
              <Plus className="w-5 h-5" /> {t.add}
            </button>
          )}

          {/* Form */}
          {showForm && (
            <ScheduledChargeForm
              formData={formData} setFormData={setFormData}
              onSubmit={handleSubmit} onCancel={resetForm}
              saving={saving} isEditing={!!editingId} t={t}
            />
          )}

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : charges.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t.empty}</p>
              <p className="text-sm">{t.emptyHint}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {charges.map(charge => (
                <ScheduledChargeItem key={charge.id} charge={charge}
                  onEdit={handleEdit} onDelete={handleDelete} t={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
